/**
 * Service schema builders.
 *
 * Single source of truth for all schema.org/Service-shaped JSON-LD across
 * the site. Every emitted node references the canonical Organization /
 * LocalBusiness / Person entities defined in src/lib/entitySchema.ts via
 * @id so search engines and AI engines can stitch the entity graph
 * together into one connected business.
 */

import {
  SCHEMA_LOCALBUSINESS_ID,
  SCHEMA_ORG_ID,
  SITE_URL,
  buildLocalBusinessSchema,
} from "@/lib/entitySchema";
import type { ServicePricing, PricingTier } from "@/lib/pricingData";

/* -------------------------------------------------------------------------- */
/*  Generic Service builder (for newer / lightweight money pages)             */
/* -------------------------------------------------------------------------- */

export type ServiceSchemaInput = {
  name: string;
  description: string;
  /** Path of the page emitting the schema, e.g. "/seo-services" */
  slug: string;
  /** schema.org serviceType taxonomy value */
  serviceType: string;
  priceRange?: string;
  category?: string;
  areaServed?: string[];
};

export function buildServiceSchema(input: ServiceSchemaInput) {
  const url = `${SITE_URL}${input.slug}`;
  const areas = input.areaServed ?? ["Colorado"];

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    name: input.name,
    description: input.description,
    url,
    serviceType: input.serviceType,
    ...(input.category ? { category: input.category } : {}),
    provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
    areaServed: areas.map((a) => ({ "@type": "AdministrativeArea", name: a })),
    ...(input.priceRange
      ? {
          offers: {
            "@type": "Offer",
            priceCurrency: "USD",
            priceSpecification: {
              "@type": "PriceSpecification",
              priceCurrency: "USD",
              price: input.priceRange,
            },
            availability: "https://schema.org/InStock",
            seller: { "@id": SCHEMA_LOCALBUSINESS_ID },
          },
        }
      : {}),
  };
}

/** Convenience: head() scripts entry for a Service schema. */
export function serviceSchemaScript(input: ServiceSchemaInput) {
  return {
    type: "application/ld+json",
    children: JSON.stringify(buildServiceSchema(input)),
  };
}

/* -------------------------------------------------------------------------- */
/*  Service Hub schemas (used by ServicePage + ServicePricingPage)            */
/* -------------------------------------------------------------------------- */

type HubServiceInput = {
  slug: string;
  serviceName: string;
  description: string;
  pricing?: ServicePricing;
};

/** Service node with optional OfferCatalog (one Offer per pricing tier). */
export function buildServiceHubServiceJsonLd(input: HubServiceInput) {
  const url = `${SITE_URL}/${input.slug}`;
  const base: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    name: input.serviceName,
    description: input.description,
    url,
    provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
    areaServed: [
      { "@type": "AdministrativeArea", name: "Colorado" },
    ],
  };

  if (input.pricing) {
    base.offers = {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: lowestTierPrice(input.pricing.tiers),
      offerCount: input.pricing.tiers.length,
      seller: { "@id": SCHEMA_LOCALBUSINESS_ID },
    };
    base.hasOfferCatalog = {
      "@type": "OfferCatalog",
      name: `${input.serviceName} Packages`,
      itemListElement: input.pricing.tiers.map((tier) => ({
        "@type": "Offer",
        name: tier.name,
        description: tier.desc,
        price: numericPrice(tier.price),
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        itemOffered: {
          "@type": "Service",
          name: `${input.serviceName} — ${tier.name}`,
          description: tier.desc,
        },
      })),
    };
  }

  return base;
}

/** Per-page LocalBusiness reference with makesOffer pointing to this Service. */
export function buildServiceHubLocalBusinessRef(input: {
  slug: string;
  serviceName: string;
}) {
  const serviceUrl = `${SITE_URL}/${input.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": SCHEMA_LOCALBUSINESS_ID,
    makesOffer: {
      "@type": "Offer",
      itemOffered: { "@id": `${serviceUrl}#service`, name: input.serviceName },
      seller: { "@id": SCHEMA_LOCALBUSINESS_ID },
    },
  };
}

/** BreadcrumbList: Home → Service [→ Pricing]. */
export function buildServiceHubBreadcrumbJsonLd(input: {
  slug: string;
  serviceName: string;
  pricing?: boolean;
}) {
  const items: Array<Record<string, unknown>> = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${SITE_URL}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: input.serviceName,
      item: `${SITE_URL}/${input.slug}`,
    },
  ];

  if (input.pricing) {
    items.push({
      "@type": "ListItem",
      position: 3,
      name: "Pricing",
      item: `${SITE_URL}/${input.slug}/pricing`,
    });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

/** FAQPage builder for arbitrary Q&A pairs. */
export function buildFaqPageJsonLd(faqs: Array<{ q: string; a: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

/* -------------------------------------------------------------------------- */
/*  Homepage-specific enrichments                                             */
/* -------------------------------------------------------------------------- */

const COLORADO_CITIES_SERVED = [
  "Colorado Springs",
  "Denver",
  "Fort Collins",
  "Boulder",
  "Aurora",
  "Pueblo",
  "Castle Rock",
  "Parker",
];

/** Re-asserts the canonical LocalBusiness with explicit per-city areaServed. */
export function buildHomepageLocalBusinessAreaServed() {
  return buildLocalBusinessSchema();
}

/** WebSite node: enables sitelinks search box and links to the Organization. */
export function buildHomepageWebSiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: "Colorado Web Impressions",
    publisher: { "@id": SCHEMA_ORG_ID },
    inLanguage: "en-US",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function numericPrice(price: string): string {
  // "$2,500" → "2500", "Custom" → "0"
  const digits = price.replace(/[^0-9.]/g, "");
  return digits.length ? digits : "0";
}

function lowestTierPrice(tiers: PricingTier[]): string {
  const numeric = tiers
    .map((t) => Number.parseFloat(numericPrice(t.price)))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (!numeric.length) return "0";
  return String(Math.min(...numeric));
}
